import { getImageUrl } from "@/lib/aws-amplify/host-storage";
import dayjs from "@/lib/dayjs";
import { getTodaysDate } from "@/lib/dayjs/get-todays-date";
import type { Voucher } from "@/lib/gql/__generated__/graphql";
import { SaleTypeForTerm } from "@/lib/gql/__generated__/graphql";

import type { DnDResultItem } from "@/hooks/use-dnd";

import { IconButtonList } from "@/components/features/items/edit/_shared/ui/components/system/list/list-item/list-icon-buttons";

import DragIndicator from "@/components/ui/icon/drag-indicator";
import { Image } from "@/components/ui/image/image";
import { TableCell, TableRow } from "@/components/ui/table/table";

import { DUMMY_IMAGE } from "@/constants/dummy-image";

import type { ComponentProps } from "react";

type Props = {
	dndEvents: DnDResultItem<Voucher>["events"] | undefined;

	voucher: Voucher;
	idx: number;

	handleClickTableRow: () => void;

	iconButtonActionsList: ComponentProps<typeof IconButtonList>;

	isSorting: boolean;
};

export const VoucherTableRow = ({
	dndEvents,

	voucher,
	idx,
	handleClickTableRow,

	iconButtonActionsList,
	isSorting,
}: Props) => {
	const {
		voucherID,
		name,
		mainKeyVisual,

		saleTypeForTerm,
		saleStart,
		saleEnd,
		sellingPrice,
		voucherPrice,
		maxStockNum,
		soldNumTotal,
	} = voucher;

	const _ref = dndEvents?.ref;
	const _onMouseDown = dndEvents?.onMouseDown;

	const isSortable = !!_ref && !!_onMouseDown;
	const ref = isSortable && isSorting ? _ref : undefined;
	const onMouseDown = isSortable && isSorting ? _onMouseDown : undefined;

	const yyyymmddConverter = (date: string) => {
		const [year, month, day] = date.split("-");
		return `${year}年${month}月${day}日`;
	};

	const imageURL = getImageUrl(mainKeyVisual) || DUMMY_IMAGE.url;

	const todaysDateDayjs = dayjs(getTodaysDate());
	const saleStartDayjs = dayjs(saleStart);
	const saleEndDayjs = dayjs(saleEnd);

	// 常に販売しているかどうか
	const isAlwaysSale = saleTypeForTerm === SaleTypeForTerm.Always;

	// 操作日が販売期間内にあるかどうか
	const isSalePeriod =
		saleTypeForTerm === SaleTypeForTerm.Limited && //
		!!saleStart &&
		!!saleEnd &&
		todaysDateDayjs.isSameOrAfter(saleStartDayjs) &&
		todaysDateDayjs.isSameOrBefore(saleEndDayjs);

	// 残在庫
	const remainingStock = maxStockNum ? maxStockNum - soldNumTotal : 0;
	const remainingStockText = isAlwaysSale || isSalePeriod ? (remainingStock ? remainingStock : "∞") : "販売期間外";

	// 販売期間
	const salePeriodText =
		saleTypeForTerm === SaleTypeForTerm.Limited && saleStart && saleEnd
			? `${yyyymmddConverter(saleStart)} 〜 ${yyyymmddConverter(saleEnd)}`
			: "常に販売";
	// 販売価格
	const sellingPriceAmountText = `${sellingPrice.amount.toLocaleString()}円（税込）`;
	// 適用価格
	const voucherPriceAmountText = `${voucherPrice.amount.toLocaleString()}円（税込）`;

	return (
		<TableRow
			key={voucherID}
			className="group cursor-pointer select-none rounded"
			ref={ref}
			onMouseDown={onMouseDown}
			onClick={() => {
				if (isSorting) return;
				handleClickTableRow();
			}}
		>
			{isSorting && isSortable && (
				<TableCell>
					<div className="grid place-content-center">
						<div className="shrink-0 rounded p-1 hover:cursor-grab hover:bg-zinc-100">
							<DragIndicator size={"default"} />
						</div>
					</div>
				</TableCell>
			)}
			<TableCell>
				<p>{idx + 1}</p>
			</TableCell>
			<TableCell>
				<Image src={imageURL} className="h-16 w-16 min-w-16 shrink-0 rounded object-cover" />
			</TableCell>
			<TableCell>
				<p className="line-clamp-3 w-52 underline-offset-4 group-hover:underline">{name}</p>
			</TableCell>
			<TableCell>
				<p className="w-20">{remainingStockText}</p>
			</TableCell>
			<TableCell>
				<p className="w-40">{salePeriodText}</p>
			</TableCell>
			<TableCell>
				<p className="w-40">{sellingPriceAmountText}</p>
			</TableCell>
			<TableCell>
				<p className="w-40">{voucherPriceAmountText}</p>
			</TableCell>
			<TableCell>
				<IconButtonList {...iconButtonActionsList} />
			</TableCell>
		</TableRow>
	);
};

VoucherTableRow.displayName = "VoucherTableRow";
